import isEmpty from 'lodash/isEmpty';
import { JobSpeciality, translateLanguages } from '@clipr/lib';
import { action, computed, makeObservable, observable } from 'mobx';
import { Store } from '../../store/store';
import { BindingProps, StoreNode } from '../../store';
import { input, inputGroup, InputGroupState, InputState } from '../../components/input';
import { UploadTask } from '../../services/upload/uploadTask';
import { UploadFile } from '../../services/upload/uploadService';
import { Team } from '../../entities';
import { DropdownItemObject } from '../../components/input/dropdownInput';

const TranslateLanguageItems: DropdownItemObject[] = translateLanguages
  .map(({ name, code }) => ({ value: code, label: name }));

type DocumentUploadFileItemProps = BindingProps<{
  teamId?: string;
  file: File;
}>;

export class DocumentUploadFileItemState
  extends StoreNode {

  constructor(store: Store, props: DocumentUploadFileItemProps) {
    super(store, props);
    makeObservable(this);

    this.sourceLanguageInput = input(this, {
      name: 'sourceLanguage',
      placeholder: 'Choose a language',
      selectorItems: TranslateLanguageItems,
      showStatusMessage: true,
      isRequired: true,
      statusMessage: (inp: InputState) =>
        inp.status === 'error' && inp.showStatusIcons ? inp.error?.toString() : '',
      error: (inp: InputState) => {
        if (isEmpty(inp.value)) return 'Required field';
      },
      export: (self: InputState) => self.normValue,
    });

    this.targetLanguageInput = input(this, {
      name: 'targetLanguage',
      placeholder: 'Choose a language',
      selectorItems: TranslateLanguageItems,
      showStatusMessage: true,
      isRequired: true,
      statusMessage: (inp: InputState) =>
        inp.status === 'error' && inp.showStatusIcons ? inp.error?.toString() : '',
      error: (inp: InputState) => {
        if (isEmpty(inp.value)) return 'Required field';
        if (inp.normValue === this.sourceLanguageInput.normValue)
          return 'Must differ from source';
      },
      export: (self: InputState) => self.normValue,
    });

    this.form = inputGroup(this, {
      name: 'documentForm',
      inputs: [
        this.sourceLanguageInput,
        this.targetLanguageInput,
      ],
      isSubmitDisabled: () => {
        return (
          this.form.isSubmitting ||
          this.form.hasTouchedErrorInputs
        );
      }
    });

    this.file = this.props.file;
    this.init();
  }

  @observable task: UploadTask | null = null;

  readonly form: InputGroupState;
  readonly file: File;

  readonly sourceLanguageInput: InputState;
  readonly targetLanguageInput: InputState;

  @computed get isStaged(): boolean {
    return !this.task;
  }

  @computed get isActive(): boolean {
    return !!this.task?.isActive;
  }

  @computed get isCompleted(): boolean {
    return !!this.task?.isCompleted;
  }

  @computed get isFailed(): boolean {
    return !!this.task?.isFailed;
  }

  @computed get showInStaging(): boolean {
    return this.isStaged;
  }

  @computed get inProgress(): boolean {
    return this.isActive || this.isFailed;
  }

  @computed get teamId(): string | null {
    return this.resolvedProps.teamId;
  }

  @computed get team(): Team | null {
    return this.store.teamManager.getTeam(this.teamId);
  }

  @action
  submit(): UploadTask {
    const formData = this.form.export();

    const uploadFile: UploadFile = {
      file: this.file,
      jobSpeciality: JobSpeciality.DocumentTranslation,
      videoType: undefined,
      sourceLanguageCode: formData.sourceLanguage || undefined,
      targetLanguageCode: formData.targetLanguage || undefined,
    };

    this.task = this.store.uploadService.createTask(uploadFile, {
      teamId: this.teamId || undefined,
    });

    return this.task;
  }

  @action
  init = () => {
    const teamLang = this.team?.defaultTranslationLanguage;
    const targetLang = teamLang || 'es';
    const sourceLang = targetLang === 'en' ? 'es' : 'en';

    const defaultSource = TranslateLanguageItems.find(item => item.value === sourceLang);
    const defaultTarget = TranslateLanguageItems.find(item => item.value === targetLang);
    if (defaultSource) this.sourceLanguageInput.loadValue(defaultSource);
    if (defaultTarget) this.targetLanguageInput.loadValue(defaultTarget);
  }
}
